import { useMemo } from 'react';

import {
  ENTITY_V2_QUERY,
  EntityV2Type,
  type GetEntityDataV2Query,
  type GetEntityDataV2QueryVariables,
} from '@revelio/data-access';

import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { FilterId, FilterType } from '../../../filters/types/filters';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { useUrqlQueryWithStatus } from '../../DataProvider/utils/data-provider-results-utils';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import { getCompanyRcidFromTreeSelectionId } from './utils/getCompanyRcidFromTreeSelectionId';

/**
 * Hook to fetch competitors for the selected primary company.
 * Returns competitor data and loading status.
 */
export const useCompetitorsFromPrimaryCompany = ({
  parameters,
}: {
  parameters: ReportParameterConfig[];
}) => {
  const { activeFilterMap } = useFilterContext();

  // Extract the first selected company ID from the primary company filter
  const selectedCompanyId = useMemo(() => {
    const companyFilter = activeFilterMap.get(FilterId.COMPANY);
    if (!companyFilter || companyFilter.type !== FilterType.TREE) {
      return null;
    }

    const treeFilter = companyFilter;
    const activeSelection = treeFilter.primarySelections?.find(
      (selection) => selection.isActive
    );

    if (!activeSelection) {
      return null;
    }

    // Extract numeric rcid from tree selection id (nested tree or TD `company.{rcid}` format)
    const companyId = getCompanyRcidFromTreeSelectionId(activeSelection.id);
    if (companyId === null) {
      return null;
    }

    return companyId;
  }, [activeFilterMap]);

  // Fetch competitors via Entity GraphQL query
  const [{ data: entityData, status }] = useUrqlQueryWithStatus<
    GetEntityDataV2Query,
    GetEntityDataV2QueryVariables
  >({
    query: ENTITY_V2_QUERY,
    variables: {
      req: {
        entID: selectedCompanyId ?? 0,
        entType: EntityV2Type.Company,
        numCompetitors: 30,
      },
    },
    pause: !selectedCompanyId || parameters.length < 2,
  });

  const competitors = useMemo(() => {
    if (
      status !== DataProviderQueryStatus.Success ||
      !entityData?.entityV2?.competitors
    ) {
      return null;
    }

    return entityData.entityV2.competitors.filter(
      (competitor) => competitor?.id !== null && competitor?.id !== undefined
    );
  }, [entityData, status]);

  return {
    competitors,
    selectedCompanyId,
    isLoading: status === DataProviderQueryStatus.Fetching,
    hasError: status === DataProviderQueryStatus.Error,
  };
};
